import { useState } from 'react';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart as faHeartOutline } from '@fortawesome/pro-regular-svg-icons/faHeart';
import { faEyeSlash } from '@fortawesome/pro-solid-svg-icons/faEyeSlash';
import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faFlag } from '@fortawesome/pro-regular-svg-icons/faFlag';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faComment } from '@fortawesome/pro-solid-svg-icons/faComment';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { FlagProfileModal } from 'us-react-components/dist/FlagProfileModal/FlagProfileModal';
import {
  Parent,
  SitterBlockParentService,
  SitterFavoritesService,
} from 'us-web-services';

import { Button, ButtonType } from '../../../components';
import { ParentNoteModal, ParentPrivateNote } from '../../MessageThread/components/ParentNoteModal';

interface ExtendedRelationship {
  blocked?: boolean;
  blockedId?: number | null;
  favorite?: { id?: number };
  note?: ParentPrivateNote;
  bookingCount?: number;
  lastMessaged?: string;
  distance?: string | number;
  previousBookingRates?: Array<{ minimumRate?: number; kidCount?: number }>;
}

export interface FamilyWithExtras extends Omit<Parent, 'relationship'> {
  relationship?: ExtendedRelationship;
}

interface Props {
  family: FamilyWithExtras;
  selectedType: string;
  sitterId: number;
  onUpdate: (updater: (f: FamilyWithExtras) => FamilyWithExtras) => void;
}

const formatDate = (date?: string): string => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const getDistanceText = (distance?: string | number): string => {
  if (distance == null || distance === '') return '';
  const d = Number(distance);
  if (Number.isNaN(d)) return `${distance}`;
  if (d <= 1) return '< 1 mile';
  return `${d} miles`;
};

const getMapLink = (addr?: { street?: string; city?: string; state?: string; zip?: string }): string => {
  if (!addr) return '';
  const q = encodeURIComponent(
    [addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(', '),
  );
  return `https://maps.google.com/?q=${q}`;
};

const NOTE_LIMIT = 300;

export const FamilyCard = ({
  family,
  selectedType,
  sitterId,
  onUpdate,
}: Props): JSX.Element => {
  const navigate = useNavigate();
  const [noteOpen, setNoteOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [noteCollapsed, setNoteCollapsed] = useState(true);

  const rel = family.relationship ?? {};
  const isFavorite = !!rel.favorite?.id;
  const isBlocked = !!rel.blocked;
  const note = rel.note;
  const { t } = useSharedTranslation(namespaces.caregiver);
  const fullName = [family.firstName, family.lastName].filter(Boolean).join(' ').trim()
    || family.firstName
    || '';

  const updateFavorite = async (): Promise<void> => {
    if (!family.id) return;
    try {
      if (isFavorite && rel.favorite?.id) {
        await SitterFavoritesService.delete(rel.favorite.id);
        onUpdate((f) => ({ ...f, relationship: { ...f.relationship, favorite: undefined } }));
        if (selectedType === 'favorite') {
          toast.success(
            t('pages.yourFamilies.removedFavoriteToast', {
              name: family.firstName ?? '',
            }),
          );
        }
      } else {
        const resp = await SitterFavoritesService.create({ sitterId, parentId: family.id });
        const created = resp.data?.data;
        onUpdate((f) => ({
          ...f,
          relationship: { ...f.relationship, favorite: { id: created?.id } },
        }));
      }
    } catch {
      toast.error(t('pages.yourFamilies.favoriteFailed'));
    }
  };

  const toggleAvailability = async (): Promise<void> => {
    if (!family.id) return;
    setMenuOpen(false);
    try {
      if (rel.blocked && rel.blockedId) {
        await SitterBlockParentService.delete(rel.blockedId);
        onUpdate((f) => ({
          ...f,
          relationship: { ...f.relationship, blocked: false, blockedId: null },
        }));
      } else {
        const resp = await SitterBlockParentService.create({ sitterId, parentId: family.id });
        const newBlockId = resp.data?.data?.id ?? null;
        onUpdate((f) => ({
          ...f,
          relationship: { ...f.relationship, blocked: true, blockedId: newBlockId },
        }));
        toast.success(
          t('pages.yourFamilies.hiddenToast', { name: family.firstName ?? '' }),
        );
      }
    } catch {
      toast.error(t('pages.yourFamilies.blockFailed'));
    }
  };

  const addBooking = (): void => {
    if (!family.id) return;
    navigate(`/sitter/booking/create?parentId=${family.id}`);
  };

  const onNoteSaved = (saved?: ParentPrivateNote): void => {
    onUpdate((f) => ({ ...f, relationship: { ...f.relationship, note: saved } }));
  };

  // Without a family.id there's nothing meaningful to link to; render dead
  // hrefs so the click is a no-op rather than navigating to a broken route.
  const profileHref = family.id ? `/app/parent/${family.id}` : undefined;
  const messagesHref = family.id ? `/app/sitter/${sitterId}/messages/${family.id}` : undefined;
  const bookingsHref = family.id
    ? `/app/bookings?filterType=all_active&parentId=${family.id}`
    : undefined;

  return (
    <div className='us-card rounded-[20px] shadow-sm'>
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
        <div className='sm:col-span-2 flex gap-4'>
          <div className='relative w-24 h-24 flex-shrink-0'>
            <a id={`parent-${family.id}`} href={profileHref}>
              {family.pictureUrl ? (
                <img
                  alt={fullName}
                  src={family.pictureUrl}
                  className='w-24 h-24 rounded object-cover'
                />
              ) : (
                <div className='w-24 h-24 rounded bg-neutral-200' />
              )}
              {isBlocked && (
                <span className='absolute inset-0 bg-white/70 flex items-center justify-center rounded'>
                  <FontAwesomeIcon icon={faEyeSlash} className='text-3xl text-neutral-700' />
                </span>
              )}
            </a>
            {!isBlocked && (
              <Button
                buttonType={ButtonType.tertiary}
                onClick={updateFavorite}
                aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                className='absolute -bottom-1 -right-1 bg-white rounded-full w-7 h-7 p-0 border border-neutral-200 flex items-center justify-center'
              >
                <FontAwesomeIcon
                  icon={isFavorite ? faHeartSolid : faHeartOutline}
                  className={isFavorite ? 'text-bubblegum' : 'text-neutral-500'}
                />
              </Button>
            )}
          </div>
          <div className='min-w-0 flex-1'>
            <a
              id='family-name-to-profile'
              href={profileHref}
              className='font-semibold text-lg text-sapphire'
            >
              {fullName}
            </a>
            {family.address && (
              <div className='text-sm'>
                <a
                  id='address-to-map'
                  target='_blank'
                  rel='noreferrer'
                  href={getMapLink(family.address)}
                  className='text-sapphire'
                >
                  {family.address.city}
                  {family.address.neighborhood ? ` - ${family.address.neighborhood}` : ''}
                </a>
                {rel.distance ? (
                  <span className='text-neutral-700'> ({getDistanceText(rel.distance)})</span>
                ) : null}
              </div>
            )}
            {!!rel.previousBookingRates?.length && ((): JSX.Element => {
              const r = rel.previousBookingRates![0];
              const kidLabel = (r.kidCount ?? 0) === 1 ? '1 child' : `${r.kidCount ?? 0} children`;
              return (
                <div className='text-sm text-neutral-700 mt-1'>
                  Rate last paid: ${r.minimumRate} for {kidLabel}
                </div>
              );
            })()}
            {!!rel.bookingCount && (
              <a
                id='num-bookings-to-list'
                href={bookingsHref}
                className='block text-sm text-sapphire mt-1'
              >
                <FontAwesomeIcon icon={faCircleCheck} className='mr-1 text-sapphire' />
                {rel.bookingCount} completed booking{rel.bookingCount > 1 ? 's' : ''} with you
              </a>
            )}
            {rel.lastMessaged && (
              <a
                id='num-messages-to-messages'
                href={messagesHref}
                className='block text-sm text-sapphire mt-1'
              >
                <FontAwesomeIcon icon={faComment} className='mr-1 text-quaternary' />
                Last message {formatDate(rel.lastMessaged)}
              </a>
            )}
          </div>
        </div>

        <div className='sm:col-span-1'>
          {note?.comment && (
            <div className='mb-2 text-sm italic text-neutral-700'>
              {noteCollapsed && note.comment.length > NOTE_LIMIT
                ? `${note.comment.slice(0, NOTE_LIMIT)}…`
                : note.comment}
              {note.comment.length > NOTE_LIMIT && (
                <Button
                  buttonType={ButtonType.tertiary}
                  className='ml-1 underline'
                  onClick={(): void => setNoteCollapsed((c) => !c)}
                >
                  {noteCollapsed
                    ? t('pages.yourFamilies.card.viewMore')
                    : t('pages.yourFamilies.card.viewLess')}
                </Button>
              )}
            </div>
          )}
          {note?.updated && (
            <div className='text-xs text-neutral-600 mb-2'>
              <FontAwesomeIcon icon={faLock} className='mr-1' />
              {formatDate(note.updated)}
            </div>
          )}
          <Button
            id='create-or-edit-note'
            buttonType={ButtonType.tertiary}
            onClick={(): void => setNoteOpen(true)}
            className='underline text-sm text-sapphire'
          >
            {!note?.comment && (
              <FontAwesomeIcon icon={faLock} className='mr-1' aria-hidden='true' />
            )}
            {note?.comment
              ? t('pages.yourFamilies.card.editNote')
              : t('pages.yourFamilies.card.savePrivateNote')}
          </Button>

          <div className='mt-3 flex gap-2 relative'>
            <Button
              id={`add-booking-${family.id}`}
              buttonType={ButtonType.secondary}
              onClick={addBooking}
            >
              {t('pages.yourFamilies.card.addBooking')}
            </Button>
            <Button
              id='family-actions-dropdown'
              buttonType={ButtonType.secondary}
              onClick={(): void => setMenuOpen((o) => !o)}
              aria-haspopup='menu'
              aria-expanded={menuOpen}
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </Button>
            {menuOpen && (
              <ul
                role='menu'
                className='absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded shadow-md z-10 min-w-48 py-1'
              >
                <li role='menuitem'>
                  <Button
                    id={`toggle-availability-${family.id}`}
                    buttonType={ButtonType.tertiary}
                    onClick={toggleAvailability}
                    className='block w-full text-left px-3 py-2 hover:bg-neutral-50'
                  >
                    <FontAwesomeIcon
                      icon={isBlocked ? faEye : faEyeSlash}
                      className='mr-2'
                    />
                    {isBlocked
                      ? t('pages.yourFamilies.card.showAvailability')
                      : t('pages.yourFamilies.card.hideAvailability')}
                  </Button>
                </li>
                <li role='menuitem'>
                  <Button
                    id='report-family'
                    buttonType={ButtonType.tertiary}
                    onClick={(): void => {
                      setMenuOpen(false);
                      setFlagOpen(true);
                    }}
                    className='block w-full text-left px-3 py-2 hover:bg-neutral-50'
                  >
                    <FontAwesomeIcon icon={faFlag} className='mr-2' />
                    {t('pages.yourFamilies.card.report')}
                  </Button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>

      {family.id && (
        <ParentNoteModal
          isOpen={noteOpen}
          onClose={(): void => setNoteOpen(false)}
          sitterId={sitterId}
          parentId={family.id}
          initialNote={note}
          onSaved={onNoteSaved}
        />
      )}
      {family.id && (
        <FlagProfileModal
          isOpen={flagOpen}
          onClose={(): void => setFlagOpen(false)}
          flaggedId={family.id}
          flaggingId={sitterId}
        />
      )}
    </div>
  );
};
